import type { AppRouter as LLMOpsAppRouter } from "@inference-net/relay/router";
import type { TRPCClient } from "@trpc/client";
import type { CreateTRPCReact } from "@trpc/react-query";
import { createContext } from "react";
import { createCallerTimezoneHeaders } from "@inference-net/cf-compatible-utils";
import {
  createTRPCReact,
  httpBatchLink as httpBatchLinkReact,
} from "@trpc/react-query";
import superjson from "superjson";
import { z } from "zod";

import type { ServerRouter } from "@kuzco/trpc-router";
import type { FetchInitEsque } from "@kuzco/utils";
import { normalizeFetchInit } from "@kuzco/utils";

import type { AppRouter } from "./server/router";

function fetchWithCredentials(
  input: RequestInfo | URL,
  init?: FetchInitEsque,
): Promise<Response> {
  const requestInit: RequestInit = {
    ...normalizeFetchInit(init),
    credentials: "include",
  };

  return fetch(input, requestInit);
}

const RELAY_TRPC_URL = `${import.meta.env.VITE_RELAY_API_URL}/api/trpc`;
const LLM_OPS_TRPC_URL = z
  .string()
  .parse(import.meta.env.VITE_LLM_OPS_TRPC_API_URL);

export type NubankClient = TRPCClient<AppRouter>;
export type RelayClient = TRPCClient<ServerRouter>;
export type LlmOpsClient = TRPCClient<LLMOpsAppRouter>;

// Each tRPC React instance must have its own React context. Without an explicit
// `context`, `@trpc/react-query` falls back to a single module-level context,
// which means whichever `<Provider>` is innermost wins and all hooks resolve
// against the same client. Passing a fresh context per instance keeps the
// nubank / relay / catalyst providers isolated.
const nubankReactContext = createContext(null);
const relayReactContext = createContext(null);
const catalystReactContext = createContext(null);

export const nubankTrpc: CreateTRPCReact<AppRouter, unknown> =
  createTRPCReact<AppRouter>({
    context: nubankReactContext,
  });

export const relayTrpc: CreateTRPCReact<ServerRouter, unknown> =
  createTRPCReact<ServerRouter>({
    context: relayReactContext,
  });

export const catalystTrpc: CreateTRPCReact<LLMOpsAppRouter, unknown> =
  createTRPCReact<LLMOpsAppRouter>({
    context: catalystReactContext,
  });

export function createNubankTrpcReactClient(): NubankClient {
  return nubankTrpc.createClient({
    links: [
      httpBatchLinkReact({
        fetch: fetchWithCredentials,
        headers: createCallerTimezoneHeaders,
        url: "/api/trpc",
      }),
    ],
  });
}

export function createRelayTrpcReactClient(): RelayClient {
  return relayTrpc.createClient({
    links: [
      httpBatchLinkReact({
        fetch: fetchWithCredentials,
        headers: createCallerTimezoneHeaders,
        transformer: superjson,
        url: RELAY_TRPC_URL,
      }),
    ],
  });
}

export function createCatalystTrpcReactClient(): LlmOpsClient {
  return catalystTrpc.createClient({
    links: [
      httpBatchLinkReact({
        fetch: fetchWithCredentials,
        headers: createCallerTimezoneHeaders,
        url: LLM_OPS_TRPC_URL,
      }),
    ],
  });
}
